import { useAppStore } from "@hooks/useAppStore";
import type { TFunction } from "i18next";

const MAX_POINTS = 30;

export type Skill = "Reading" | "Listening" | "Speaking" | "Writing";

interface SkillScore {
	skill: Skill;
	numPointsEarned: number;
	numTotalPoints: number;
}

export interface SkillDetails {
	skill: Skill;
	scaledScore: number;
	level: string;
	numTotalPoints: number;
	numPointsEarned: number;
	compositeColor: string;
}

type BaseSkillLevel = "lowIntermediate" | "highIntermediate" | "advanced";
type OptionalSkillLevel = "basic" | "belowBasic" | "belowLowIntermediate";
type SkillLevel = BaseSkillLevel | OptionalSkillLevel;

type SkillCutoffs = {
	[K in BaseSkillLevel]: { min: number; max: number };
} & Partial<{
	[K in OptionalSkillLevel]: { min: number; max: number };
}>;

export const skillCutoffs: Record<string, SkillCutoffs> = {
	Reading: {
		belowLowIntermediate: { min: 0, max: 3 },
		lowIntermediate: { min: 4, max: 17 },
		highIntermediate: { min: 18, max: 23 },
		advanced: { min: 24, max: 30 },
	},
	Listening: {
		belowLowIntermediate: { min: 0, max: 8 },
		lowIntermediate: { min: 9, max: 16 },
		highIntermediate: { min: 17, max: 21 },
		advanced: { min: 22, max: 30 },
	},
	Speaking: {
		belowBasic: { min: 0, max: 9 },
		basic: { min: 10, max: 15 },
		lowIntermediate: { min: 16, max: 19 },
		highIntermediate: { min: 20, max: 24 },
		advanced: { min: 25, max: 30 },
	},
	Writing: {
		belowBasic: { min: 0, max: 6 },
		basic: { min: 7, max: 12 },
		lowIntermediate: { min: 13, max: 16 },
		highIntermediate: { min: 17, max: 23 },
		advanced: { min: 24, max: 30 },
	},
};

function normalizeScore(rawScore: number, maxPoints: number) {
	const score = (rawScore / maxPoints) * MAX_POINTS;
	return Math.min(score, MAX_POINTS);
}

function getProficiencyLevel(
	skill: string,
	rawScore: number,
	maxPoints: number,
): { scaledScore: number; level: SkillLevel } {
	const scaledScore = normalizeScore(rawScore, maxPoints);
	const cutoffs = skillCutoffs[skill];

	// Sort levels by min score to ensure proper order
	const sortedLevels = Object.entries(cutoffs).sort(
		(a, b) => a[1].min - b[1].min,
	);

	for (const [level, range] of sortedLevels) {
		if (scaledScore < range.min) {
			// Return the previous level or the first level if we're at the start
			const prevLevel = sortedLevels[0][0];
			return {
				scaledScore: Number.parseFloat(scaledScore.toFixed(2)),
				level: prevLevel as SkillLevel,
			};
		}
		if (scaledScore <= range.max) {
			return {
				scaledScore: Number.parseFloat(scaledScore.toFixed(2)),
				level: level as SkillLevel,
			};
		}
	}
	return { scaledScore: 0, level: "basic" };
}

function proficiencyToNumeric(level: string): number {
	switch (level) {
		case "advanced":
			return 4;
		case "highIntermediate":
			return 3;
		case "lowIntermediate":
			return 2;
		default:
			return 1;
	}
}

/**
 * Convert back from a numeric scale to an overall proficiency name.
 * E.g., average all skill levels and map to final bracket as you see fit.
 */
function numericToProficiency(value: number): SkillLevel {
	if (value >= 3.5) {
		return "advanced";
	}
	if (value >= 2.5) {
		return "highIntermediate";
	}
	if (value >= 1.5) {
		return "lowIntermediate";
	}
	return "basic";
}

function proficiencyToColor(level: SkillLevel): string {
	const sortedMasteryLevels = useAppStore.getState().app.masteryLevels!;
	switch (level) {
		case "advanced": {
			const masteryLevel = sortedMasteryLevels.find(
				(level) => level.id === "advanced",
			);
			return masteryLevel?.lightColor!;
		}
		case "highIntermediate": {
			const masteryLevel = sortedMasteryLevels.find(
				(level) => level.id === "highIntermediate",
			);
			return masteryLevel?.lightColor!;
		}
		case "lowIntermediate": {
			const masteryLevel = sortedMasteryLevels.find(
				(level) => level.id === "lowIntermediate",
			);
			return masteryLevel?.lightColor!;
		}
		default: {
			const masteryLevel = sortedMasteryLevels.find(
				(level) => level.id === "basic",
			);
			return masteryLevel?.lightColor!;
		}
	}
}

function getCompositeFeedback(level: SkillLevel, t: TFunction): string {
	switch (level) {
		case "advanced":
			return t("report.pointsInfo.feedbackText.advanced");
		case "highIntermediate":
			return t("report.pointsInfo.feedbackText.highIntermediate");
		case "lowIntermediate":
			return t("report.pointsInfo.feedbackText.lowIntermediate");
		default:
			return t("report.pointsInfo.feedbackText.basic");
	}
}

// Composite score calculation handles arbitrary points
export function getCompositeScoreAndLevel(skills: SkillScore[], t: TFunction) {
	let totalNumeric = 0;
	let totalScaledScore = 0;

	const skillDetails: SkillDetails[] = skills.map(
		({ skill, numPointsEarned, numTotalPoints }) => {
			// Normalize and determine level
			const { scaledScore, level } = getProficiencyLevel(
				skill,
				numPointsEarned,
				numTotalPoints,
			);
			// Convert level to numeric
			const numeric = proficiencyToNumeric(level);

			totalNumeric += numeric;
			totalScaledScore += scaledScore;

			return {
				skill,
				scaledScore,
				level,
				numTotalPoints,
				numPointsEarned,
				compositeColor: proficiencyToColor(level),
			};
		},
	);

	// Calculate the average numeric proficiency level
	const compositeNumeric = totalNumeric / skills.length;
	const compositeLevel = numericToProficiency(compositeNumeric);
	const compositeColor = proficiencyToColor(compositeLevel);
	const compositeFeedback = `${getCompositeFeedback(compositeLevel, t)}`;
	const compositeScaledScore = Math.min(
		Math.round(totalScaledScore / skills.length),
		MAX_POINTS,
	);

	return {
		skillDetails,
		compositeNumeric: Number.parseFloat(compositeNumeric.toFixed(2)),
		compositeLevel,
		compositeColor,
		compositeFeedback,
		compositeScaledScore,
	};
}
